// src/components/ShopDetails.js - Version avec Modal
import React, { useState, useEffect } from 'react';
import { Globe, Calendar, Clock, Edit2, Star } from 'lucide-react';
import ShopStepEditor from './ShopStepEditor';

const ShopDetails = ({ 
  shop, 
  hasPermission, 
  onValidate, 
  onQuickValidate,
  formatDate,
  apiUrl,
  token,
  editMode: externalEditMode,
  onEditModeChange
}) => {
  const [editMode, setEditMode] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showPhoneDetails, setShowPhoneDetails] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAvis, setShowAvis] = useState(false);
  const [avis, setAvis] = useState([]);

  // Synchroniser l'état d'édition avec les props externes si fourni
  useEffect(() => {
    if (externalEditMode !== undefined) {
      setEditMode(externalEditMode);
    }
  }, [externalEditMode]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (shop) {
      loadAvis();
    }
  }, [shop]);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${apiUrl}/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadAvis = async () => {
    try {
      const response = await fetch(`${apiUrl}/shops/${shop._id}/avis`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setAvis(data.avis || []);
    } catch (error) {
      console.error('Error loading avis:', error);
      setAvis([]);
    }
  };

  const handleSave = async (editedShop) => {
    try {
      delete editedShop.avis;// Do not save avis as we handle them separately
      const response = await fetch(`${apiUrl}/shops/${editedShop._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedShop)
      });

      if (response.ok) {
        window.location.reload();
      } else {
        const error = await response.json();
        throw new Error(error.error);
      }
    } catch (error) {
      console.error('Error saving shop:', error);
      throw error;
    }
  };

  const handleEdit = () => {
    if (!hasPermission('edit')) {
      alert('Vous n\'avez pas la permission d\'éditer des boutiques');
      return;
    }
    const newEditMode = true;
    setEditMode(newEditMode);
    if (onEditModeChange) {
      onEditModeChange(newEditMode);
    }
  };

  const handleCancelEdit = () => {
    const newEditMode = false;
    setEditMode(newEditMode);
    if (onEditModeChange) {
      onEditModeChange(newEditMode);
    }
  };

  const handleModalClick = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      handleCancelEdit();
    }
  };

  const calculateAverageNote = () => {
    if (!avis || avis.length === 0) return 0;
    const sum = avis.reduce((acc, av) => acc + av.note, 0);
    return (sum / avis.length).toFixed(1);
  };

  if (!shop) {
    return (
      <div className="card">
        <div className="card-header">
          <h2>Détails de la boutique</h2>
        </div>
        <div className="details-content">
          <div className="empty-state">
            Sélectionnez une boutique pour voir les détails
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2>Détails de la boutique</h2>
        </div>
        <div className="details-content">
          <div className="details">
            {/* Logo */}
            <div className="detail-item logo-section">
              <label>Logo</label>
              {shop.logo ? (
                <img 
                  src={shop.logo} 
                  alt={shop.name} 
                  className="shop-logo-large" 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="logo-placeholder-large" style={{ display: shop.logo ? 'none' : 'flex' }}>
                <Globe className="placeholder-icon-large" />
                <span>Aucun logo</span>
              </div>
            </div>

            {/* Informations de base */}
            <div className="detail-item">
              <label>Nom</label>
              <p>{shop.name || <span className="empty">Non renseigné</span>}</p>
            </div>

            <div className="detail-item">
              <label>Description</label>
              <p>{shop.description || <span className="empty">Non renseignée</span>}</p>
            </div>

            <div className="detail-item">
              <label>Domaine</label>
              <p>
                <a 
                  href={shop.domain.startsWith('http') ? shop.domain : `https://${shop.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="domain-link-detail"
                >
                  {shop.domain} 🔗
                </a>
              </p>
            </div>

            {/* Catégories */}
            <div className="detail-item">
              <label>Catégories</label>
              <div className="categories-display">
                {shop.categories && shop.categories.length > 0 ? (
                  shop.categories.map((catId, idx) => {
                    const cat = categories.find(c => c._id === catId);
                    return cat ? (
                      <span key={idx} className="category-badge" style={{ backgroundColor: cat.color }}>
                        {cat.name}
                      </span>
                    ) : null;
                  })
                ) : (
                  <span className="empty">Aucune catégorie</span>
                )}
              </div>
            </div>

            {/* Avis */}
            <div className="detail-section">
              <div className="section-header">
                <label>⭐ Avis clients ({avis.length})</label>
                <button
                  onClick={() => setShowAvis(!showAvis)}
                  className="btn-link"
                >
                  {showAvis ? 'Masquer' : 'Afficher'}
                </button>
              </div>
              {avis.length > 0 && (
                <div className="avis-summary">
                  <Star className="star-icon-filled" />
                  <span className="average-note">{calculateAverageNote()}/5</span>
                  <span className="avis-count">({avis.length} avis)</span>
                </div>
              )}
              {showAvis && avis.length > 0 && (
                <div className="avis-list">
                  {avis.slice(0, 5).map((av, idx) => (
                    <div key={idx} className="avis-item">
                      <div className="avis-header">
                        <div className="avis-stars">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              size={14} 
                              className={i < av.note ? 'star-filled' : 'star-empty'}
                            />
                          ))}
                        </div>
                        <span className="avis-author">{av.firstname} {av.lastname}</span>
                      </div>
                      <p className="avis-content">{av.content}</p>
                    </div>
                  ))}
                  {avis.length > 5 && (
                    <p className="more">... et {avis.length - 5} autres avis</p>
                  )}
                </div>
              )}
              {avis.length === 0 && showAvis && (
                <p className="empty">Aucun avis pour le moment</p>
              )}
            </div>

            {/* Téléphone principal */}
            <div className="detail-section">
              <div className="section-header">
                <label>Téléphone principal (SAV)</label>
              </div>
              {shop.mainPhone ? (
                <div className="main-phone-badge">
                  <span className="star-icon">⭐</span>
                  <div>
                    <p className="main-phone-label">Numéro du SAV</p>
                    <p className="main-phone-number">{shop.mainPhone}</p>
                  </div>
                </div>
              ) : (
                <p className="empty">Non sélectionné</p>
              )}
            </div>

            {/* Tous les téléphones */}
            {shop.phones && shop.phones.length > 0 && (
              <div className="detail-section">
                <div className="section-header">
                  <label>Tous les téléphones ({shop.phones.length})</label>
                  <button
                    onClick={() => setShowPhoneDetails(!showPhoneDetails)}
                    className="btn-link"
                  >
                    {showPhoneDetails ? 'Masquer' : 'Afficher'}
                  </button>
                </div>
                {showPhoneDetails && (
                  <div className="compact-list">
                    <div className="compact-list-header">
                      <span>Numéro</span>
                      <span>Score</span>
                      <span>Source</span>
                    </div>
                    {shop.phones.slice(0, 20).map((phone, idx) => (
                      <div key={idx} className="compact-list-item">
                        <span>{phone.normalized}</span>
                        <span>{phone.score}</span>
                        <span>{phone.source}</span>
                      </div>
                    ))}
                    {shop.phones.length > 20 && (
                      <div className="compact-list-more">
                        ... et {shop.phones.length - 20} autres numéros
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Adresse principale */}
            <div className="detail-section">
              <label>Adresse principale</label>
              <p>{shop.mainAddress || <span className="empty">Non sélectionnée</span>}</p>
            </div>

            {/* Numéro premium */}
            {(shop.premiumPhone || shop.premiumRate) && (
              <div className="detail-section premium-section">
                <label>💰 Numéro surtaxé</label>
                {shop.premiumPhone && (
                  <div className="premium-phone">
                    <strong>Numéro :</strong> {shop.premiumPhone}
                  </div>
                )}
                {shop.premiumRate && (
                  <div className="premium-rate">
                    <strong>Tarif :</strong> {shop.premiumRate}
                  </div>
                )}
              </div>
            )}

            {/* Informations SEO */}
            {(shop.meta_title || shop.meta_keywords?.length > 0 || shop.rcs) && (
              <div className="detail-section">
                <label>SEO & Informations légales</label>
                {shop.meta_title && (
                  <div className="detail-item">
                    <label>Meta Title</label>
                    <p>{shop.meta_title}</p>
                  </div>
                )}
                {shop.meta_keywords?.length > 0 && (
                  <div className="detail-item">
                    <label>Meta Keywords</label>
                    <p>{shop.meta_keywords.join(', ')}</p>
                  </div>
                )}
                {shop.rcs && (
                  <div className="detail-item">
                    <label>RCS</label>
                    <p>{shop.rcs}</p>
                  </div>
                )}
              </div>
            )}

            {/* Stats de scraping */}
            {shop.stats && (
              <div className="detail-section">
                <label>Statistiques du scraping</label>
                <div className="stats-mini-grid">
                  <div className="mini-stat">
                    <div>Pages analysées</div>
                    <div className="value">{shop.stats.pagesAnalyzed}</div>
                  </div>
                  <div className="mini-stat">
                    <div>Pages en erreur</div>
                    <div className="value">{shop.stats.pagesError}</div>
                  </div>
                </div>
                <div className="duration">
                  <Clock className="small-icon" />
                  Durée: {shop.duration?.toFixed(2)}s
                </div>
              </div>
            )}

            {/* Historique */}
            <div className="detail-section">
              <label>Historique</label>
              <div className="timeline">
                <div className="timeline-item">
                  <Calendar className="timeline-icon" />
                  <span>Scrapé le: {formatDate(shop.scrapedAt)}</span>
                </div>
                {shop.aiProcessedAt && (
                  <div className="timeline-item">
                    <Calendar className="timeline-icon" />
                    <span>Traité par IA: {formatDate(shop.aiProcessedAt)}</span>
                  </div>
                )}
                {shop.validatedAt && (
                  <div className="timeline-item">
                    <Calendar className="timeline-icon" />
                    <span>
                      Validé: {formatDate(shop.validatedAt)}
                      {shop.validatedBy && ` par ${shop.validatedBy}`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Erreurs */}
            {shop.errors && shop.errors.length > 0 && (
              <div className="detail-section">
                <label>Erreurs détectées</label>
                <div className="error-list">
                  {shop.errors.map((error, idx) => (
                    <p key={idx}>• {error}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Historique des modifications */}
            {shop.history && shop.history.length > 0 && (
              <div className="detail-section">
                <div className="section-header">
                  <label>📜 Historique des modifications ({shop.history.length})</label>
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="btn-link"
                  >
                    {showHistory ? 'Masquer' : 'Afficher'}
                  </button>
                </div>
                {showHistory && (
                  <div className="history-list">
                    {shop.history.slice().reverse().slice(0, 10).map((entry, idx) => (
                      <div key={idx} className="history-item">
                        <div className="history-header">
                          <span className="history-action">{entry.action}</span>
                          <span className="history-date">{formatDate(entry.at)}</span>
                        </div>
                        <div className="history-details">
                          <span className="history-user">Par: {entry.by}</span>
                          {entry.field && (
                            <span className="history-field">Champ: {entry.field}</span>
                          )}
                        </div>
                        {entry.comment && (
                          <p className="history-comment">{entry.comment}</p>
                        )}
                      </div>
                    ))}
                    {shop.history.length > 10 && (
                      <p className="more">... et {shop.history.length - 10} autres modifications</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="actions">
              {hasPermission('edit') && (
                <button onClick={handleEdit} className="btn btn-primary full">
                  <Edit2 className="icon" />
                  Éditer la boutique
                </button>
              )}

              {shop.status === 'scrapped' && hasPermission('validate') && (
                <button
                  onClick={() => onQuickValidate(shop._id)}
                  className="btn btn-success full"
                >
                  Valider directement
                </button>
              )}
              
              {shop.status === 'review' && hasPermission('validate') && (
                <div className="validation-actions">
                  <button
                    onClick={() => onValidate(shop._id, true)}
                    className="btn btn-success"
                  >
                    Valider
                  </button>
                  <button
                    onClick={() => onValidate(shop._id, false)}
                    className="btn btn-danger"
                  >
                    Refuser
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal d'édition */}
      {editMode && (
        <div className="modal-overlay" onClick={handleModalClick}>
          <div className="modal-content">
            <button 
              onClick={handleCancelEdit}
              className="modal-close-btn"
              aria-label="Fermer"
            >
            
            </button>
            <ShopStepEditor
              shop={shop}
              onSave={handleSave}
              onCancel={handleCancelEdit}
              apiUrl={apiUrl}
              token={token}
              categories={categories}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ShopDetails;